import React from 'react'
import styled from 'styled-components'

const CrosshairContainer = styled.div<{
  $x: number
  $y: number
  $scale: number
}>`
  position: absolute;
  left: ${({ $x }) => $x}px;
  top: ${({ $y }) => $y}px;
  transform: translate(-50%, -50%) scale(${({ $scale }) => $scale});
  pointer-events: none;
  z-index: 10;
  opacity: 0.8;
`

const CrosshairCircle = styled.div`
  width: 1.5rem;
  height: 1.5rem;
  border: 0.125rem solid rgb(255, 255, 255, 0.75);
  border-radius: 50%;
  position: relative;
  background-color: rgba(0, 0, 0, 0.2);
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.2);
`

const CrosshairLine = styled.div<{ $isVertical?: boolean }>`
  position: absolute;
  background-color: rgb(255, 255, 255, 0.75);
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.2);

  ${({ $isVertical }) =>
    $isVertical
      ? `
    width: 0.125rem;
    height: 1rem;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
  `
      : `
    width: 1rem;
    height: 0.125rem;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
  `}
`

interface PhotoCrosshairProps {
  x: number
  y: number
  scale?: number
  containerWidth?: number
  containerHeight?: number
  imageWidth?: number
  imageHeight?: number
  originalImageWidth?: number
  originalImageHeight?: number
}

export const PhotoCrosshair: React.FC<PhotoCrosshairProps> = ({
  x,
  y,
  scale = 1,
  containerWidth,
  containerHeight,
  imageWidth,
  imageHeight,
  originalImageWidth,
  originalImageHeight,
}) => {
  // Calculate the position accounting for image scaling within container
  const calculatePosition = () => {
    if (containerWidth && containerHeight && imageWidth && imageHeight) {
      // Use original image dimensions if available, otherwise fall back to current dimensions
      const effectiveImageWidth = originalImageWidth || imageWidth
      const effectiveImageHeight = originalImageHeight || imageHeight

      // Calculate how the image is scaled to fit within the container
      const containerAspectRatio = containerWidth / containerHeight
      const imageAspectRatio = effectiveImageWidth / effectiveImageHeight

      let scaledImageWidth: number
      let scaledImageHeight: number
      let offsetX: number
      let offsetY: number

      if (imageAspectRatio > containerAspectRatio) {
        // Image is wider than container aspect ratio - constrained by width
        scaledImageWidth = containerWidth
        scaledImageHeight = containerWidth / imageAspectRatio
        offsetX = 0
        offsetY = (containerHeight - scaledImageHeight) / 2
      } else {
        // Image is taller than container aspect ratio - constrained by height
        scaledImageHeight = containerHeight
        scaledImageWidth = containerHeight * imageAspectRatio
        offsetX = (containerWidth - scaledImageWidth) / 2
        offsetY = 0
      }

      // Calculate crosshair position within the scaled image
      const crosshairX = offsetX + (x / effectiveImageWidth) * scaledImageWidth
      const crosshairY =
        offsetY + (y / effectiveImageHeight) * scaledImageHeight

      return { x: crosshairX, y: crosshairY }
    }

    // Fallback to direct positioning if dimensions not provided
    return { x, y }
  }

  const position = calculatePosition()

  return (
    <CrosshairContainer $x={position.x} $y={position.y} $scale={scale}>
      <CrosshairCircle>
        <CrosshairLine />
        <CrosshairLine $isVertical />
      </CrosshairCircle>
    </CrosshairContainer>
  )
}
